/** `state` key carrying the origin + path the sign-in started from. */
export const RETURN_TARGET_STATE_KEY = '__returnTo'

const TESTING_HOST = 'monorepo-rp-customer-profile-testing.vercel.app'

// Deployment hosts of a Vercel project: `-<hash>-` per deployment and
// `-git-<branch>-` per branch. Each project has its own name prefix, so a
// pattern matches its own project only. The production one cannot match
// `-testing-`, `-acceptance-` or `-china-poc-`: after the shared prefix it
// accepts only `git-` or a bare 9-character hash.
const TESTING_PREVIEW_HOST =
  /^monorepo-rp-customer-profile-testing-(?:git-[a-z0-9-]+|[a-z0-9]{9})-suitsupply-team\.vercel\.app$/
const PRODUCTION_PREVIEW_HOST =
  /^monorepo-rp-customer-profile-(?:git-[a-z0-9-]+|[a-z0-9]{9})-suitsupply-team\.vercel\.app$/

/** Whether a build of `env` may relay a sign-in to `url`: its own project's previews. */
function isRelayTarget(url: URL, env: string): boolean {
  if (url.protocol !== 'https:' || url.port !== '') {
    return false
  }

  if (env === 'tst') {
    return (
      url.hostname === TESTING_HOST || TESTING_PREVIEW_HOST.test(url.hostname)
    )
  }

  return env === 'prd' && PRODUCTION_PREVIEW_HOST.test(url.hostname)
}

/** Where to send the user after sign-in: the page they opened, origin included. */
export function buildReturnTarget(
  location: Pick<Location, 'origin' | 'pathname'>,
): string {
  return `${location.origin}${location.pathname}`
}

/**
 * Entra only returns to the registered redirect URI, so a preview's sign-in
 * lands on the testing or the production host. That host relays the user on to
 * the preview that started it — but only to previews of its own project
 * (testing build → testing previews, production build → production previews).
 * `state` is attacker-controlled, so anything else falls back to the current
 * origin instead of being followed.
 */
export function resolveReturnTarget(
  returnTo: unknown,
  currentOrigin: string,
  env: string,
): URL {
  const fallback = new URL('/', currentOrigin)

  if (typeof returnTo !== 'string') {
    return fallback
  }

  let target: URL
  try {
    target = new URL(returnTo)
  } catch {
    return fallback
  }

  if (target.origin === currentOrigin) {
    return new URL(target.pathname, currentOrigin)
  }

  if (isRelayTarget(target, env)) {
    return new URL(target.pathname, target.origin)
  }

  return fallback
}
